/**
 * LP Dashboard — Portfolio Performance Overview (IC-3D.1)
 *
 * Information-dense dashboard for LP Viewer role:
 *   - Summary cards: Total Committed, Capital Called, Distributed, Net, TVPI, DPI
 *   - Fund performance table (per-fund: commitment, called, distributed, TVPI)
 *   - Fund performance bar chart (Recharts)
 *   - ESG summary section (taxonomy aligned %, PAI coverage)
 *   - Report library: list of available reports
 *
 * All data fetched from /api/v1/impact-core/dashboard/lp-dashboard via react-query.
 */

import { useQuery } from '@tanstack/react-query';
import {
  Landmark,
  TrendingUp,
  ArrowDownRight,
  ArrowUpRight,
  Leaf,
  ShieldCheck,
  FileText,
  Download,
  RefreshCw,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { apiClient } from '@/api/client';

// ─── Types ────────────────────────────────────────────────────

interface LPFund {
  fund_id: number;
  fund_name: string;
  sfdr_category: string | null;
  fund_status: string | null;
  commitment_eur: number;
  capital_called_eur: number;
  distributed_eur: number;
  tvpi: number;
}

interface ESGFundSummary {
  fund_id: number;
  fund_name: string;
  taxonomy_aligned_pct: number;
  avg_revenue_aligned_pct: number;
  total_taxonomy_assessments: number;
  pai_indicators_covered: number;
  pai_coverage_pct: number;
  avg_pai_data_coverage: number;
}

interface LPReport {
  id: number;
  fund_id: number;
  regulation: string;
  filing_type: string;
  reporting_period: string;
  status: string;
  version: number;
  created_at: string | null;
  fund_name: string | null;
}

interface LPDashboardData {
  total_committed_eur: number;
  total_called_eur: number;
  total_distributed_eur: number;
  net_position_eur: number;
  tvpi: number;
  dpi: number;
  fund_count: number;
  allocation_count: number;
  funds: LPFund[];
  esg_summary: ESGFundSummary[];
  reports: LPReport[];
}

// ─── Data Fetching ────────────────────────────────────────────

function useLPDashboard() {
  return useQuery<LPDashboardData>({
    queryKey: ['ic', 'dashboard', 'lp-dashboard'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/dashboard/lp-dashboard');
      return res.data?.data ?? res.data;
    },
    retry: 1,
    staleTime: 60_000,
  });
}

// ─── Constants ────────────────────────────────────────────────

const CHART_COLORS = ['#3B82F6', '#8B5CF6', '#10B981', '#06B6D4', '#F59E0B', '#EC4899', '#64748B'];

const FALLBACK: LPDashboardData = {
  total_committed_eur: 0,
  total_called_eur: 0,
  total_distributed_eur: 0,
  net_position_eur: 0,
  tvpi: 0,
  dpi: 0,
  fund_count: 0,
  allocation_count: 0,
  funds: [],
  esg_summary: [],
  reports: [],
};

// ─── Main Component ──────────────────────────────────────────

export function LPDashboard() {
  const query = useLPDashboard();
  const data = query.data ?? FALLBACK;

  return (
    <div className="space-y-6">
      {/* Section: Key Metrics */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <MetricCard
          label="Total Committed"
          value={formatEur(data.total_committed_eur)}
          icon={<Landmark className="h-4 w-4" />}
          loading={query.isLoading}
        />
        <MetricCard
          label="Capital Called"
          value={formatEur(data.total_called_eur)}
          icon={<ArrowUpRight className="h-4 w-4" />}
          loading={query.isLoading}
        />
        <MetricCard
          label="Distributed"
          value={formatEur(data.total_distributed_eur)}
          icon={<ArrowDownRight className="h-4 w-4" />}
          loading={query.isLoading}
        />
        <MetricCard
          label="Net Position"
          value={formatEur(data.net_position_eur)}
          icon={<TrendingUp className="h-4 w-4" />}
          loading={query.isLoading}
        />
        <MetricCard
          label="TVPI"
          value={data.tvpi > 0 ? `${data.tvpi}x` : '--'}
          icon={<TrendingUp className="h-4 w-4" />}
          loading={query.isLoading}
          highlight={data.tvpi >= 1.0}
        />
        <MetricCard
          label="DPI"
          value={data.dpi > 0 ? `${data.dpi}x` : '--'}
          icon={<ArrowDownRight className="h-4 w-4" />}
          loading={query.isLoading}
        />
      </div>

      {/* Section: Fund Performance Table + Chart */}
      <div className="grid gap-4 lg:grid-cols-5">
        {/* Fund Performance Table */}
        <div className="col-span-3 rounded-lg border border-ic-border bg-ic-card p-4">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
            Fund Performance
          </h3>
          {query.isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => <SkeletonBlock key={i} className="h-10" />)}
            </div>
          ) : data.funds.length === 0 ? (
            <EmptyState message="No fund allocations found" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs ic-table">
                <thead>
                  <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                    <th className="pb-2 pr-4 font-medium">Fund</th>
                    <th className="pb-2 px-2 font-medium text-right">Commitment</th>
                    <th className="pb-2 px-2 font-medium text-right">Called</th>
                    <th className="pb-2 px-2 font-medium text-right">Distributed</th>
                    <th className="pb-2 px-2 font-medium text-right">TVPI</th>
                    <th className="pb-2 pl-2 font-medium text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.funds.map((fund) => (
                    <tr
                      key={fund.fund_id}
                      className="border-b border-ic-border/50 last:border-0"
                    >
                      <td className="py-2.5 pr-4">
                        <div className="text-xs font-medium text-ic-text-primary">{fund.fund_name}</div>
                        <div className="text-[10px] text-ic-text-tertiary">{fund.sfdr_category ?? '--'}</div>
                      </td>
                      <td className="py-2.5 px-2 text-right font-mono text-xs text-ic-text-secondary">
                        {formatEur(fund.commitment_eur)}
                      </td>
                      <td className="py-2.5 px-2 text-right font-mono text-xs text-ic-text-secondary">
                        {formatEur(fund.capital_called_eur)}
                      </td>
                      <td className="py-2.5 px-2 text-right font-mono text-xs text-ic-text-secondary">
                        {formatEur(fund.distributed_eur)}
                      </td>
                      <td className="py-2.5 px-2 text-right font-mono text-xs font-semibold text-ic-text-primary">
                        {fund.tvpi > 0 ? `${fund.tvpi}x` : '--'}
                      </td>
                      <td className="py-2.5 pl-2 text-center">
                        <StatusBadge status={fund.fund_status ?? 'unknown'} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Fund Performance Chart */}
        <div className="col-span-2 rounded-lg border border-ic-border bg-ic-card p-4">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
            Commitment by Fund
          </h3>
          {query.isLoading ? (
            <SkeletonBlock className="h-56" />
          ) : data.funds.length === 0 ? (
            <EmptyState message="No fund data" />
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data.funds.map((f) => ({
                    name: f.fund_name.length > 15 ? f.fund_name.slice(0, 15) + '...' : f.fund_name,
                    commitment: f.commitment_eur,
                    called: f.capital_called_eur,
                    distributed: f.distributed_eur,
                  }))}
                  margin={{ left: 10, right: 10, top: 5, bottom: 5 }}
                >
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10, fill: '#94A3B8' }}
                    tickLine={false}
                    axisLine={false}
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={50}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: '#64748B' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => formatCompact(v)}
                    width={50}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1E293B',
                      border: '1px solid #334155',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#E2E8F0',
                    }}
                    formatter={(value: number) => [formatEur(value), '']}
                  />
                  <Bar dataKey="commitment" name="Commitment" radius={[4, 4, 0, 0]} barSize={20}>
                    {data.funds.map((_entry, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length] ?? '#64748B'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Section: ESG Summary */}
      <div className="rounded-lg border border-ic-border bg-ic-card p-4">
        <div className="mb-3 flex items-center gap-2">
          <Leaf className="h-4 w-4 text-emerald-400" />
          <h3 className="text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
            ESG Summary by Fund
          </h3>
        </div>
        {query.isLoading ? (
          <div className="space-y-2">
            {[1, 2].map((i) => <SkeletonBlock key={i} className="h-12" />)}
          </div>
        ) : data.esg_summary.length === 0 ? (
          <EmptyState message="No ESG data available" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs ic-table">
              <thead>
                <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                  <th className="pb-2 pr-4 font-medium">Fund</th>
                  <th className="pb-2 px-2 font-medium text-center">
                    <div className="flex items-center justify-center gap-1">
                      <ShieldCheck className="h-3 w-3" />
                      Taxonomy Aligned
                    </div>
                  </th>
                  <th className="pb-2 px-2 font-medium text-center">Revenue Aligned</th>
                  <th className="pb-2 px-2 font-medium text-center">Assessments</th>
                  <th className="pb-2 px-2 font-medium text-center">
                    <div className="flex items-center justify-center gap-1">
                      <ShieldCheck className="h-3 w-3" />
                      PAI Coverage
                    </div>
                  </th>
                  <th className="pb-2 px-2 font-medium text-center">Indicators</th>
                  <th className="pb-2 pl-2 font-medium text-center">Data Coverage</th>
                </tr>
              </thead>
              <tbody>
                {data.esg_summary.map((esg) => (
                  <tr
                    key={esg.fund_id}
                    className="border-b border-ic-border/50 last:border-0"
                  >
                    <td className="py-2.5 pr-4 text-xs font-medium text-ic-text-primary">
                      {esg.fund_name}
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      <PercentBadge value={esg.taxonomy_aligned_pct} />
                    </td>
                    <td className="py-2.5 px-2 text-center font-mono text-xs text-ic-text-secondary">
                      {esg.avg_revenue_aligned_pct}%
                    </td>
                    <td className="py-2.5 px-2 text-center text-xs text-ic-text-secondary">
                      {esg.total_taxonomy_assessments}
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      <PercentBadge value={esg.pai_coverage_pct} />
                    </td>
                    <td className="py-2.5 px-2 text-center font-mono text-xs text-ic-text-secondary">
                      {esg.pai_indicators_covered}/14
                    </td>
                    <td className="py-2.5 pl-2 text-center font-mono text-xs text-ic-text-secondary">
                      {esg.avg_pai_data_coverage}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Section: Report Library */}
      <div className="rounded-lg border border-ic-border bg-ic-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-blue-400" />
            <h3 className="text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
              Report Library
            </h3>
          </div>
          <button
            onClick={() => query.refetch()}
            className="text-ic-text-tertiary hover:text-ic-text-secondary transition-colors"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${query.isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {query.isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <SkeletonBlock key={i} className="h-10" />)}
          </div>
        ) : data.reports.length === 0 ? (
          <EmptyState message="No reports available yet" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs ic-table">
              <thead>
                <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                  <th className="pb-2 pr-4 font-medium">Fund</th>
                  <th className="pb-2 px-2 font-medium">Regulation</th>
                  <th className="pb-2 px-2 font-medium">Type</th>
                  <th className="pb-2 px-2 font-medium">Period</th>
                  <th className="pb-2 px-2 font-medium text-center">Status</th>
                  <th className="pb-2 px-2 font-medium text-center">Version</th>
                  <th className="pb-2 pl-2 font-medium text-right">Created</th>
                  <th className="pb-2 pl-2 font-medium text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.reports.map((report) => (
                  <tr
                    key={report.id}
                    className="border-b border-ic-border/50 last:border-0"
                  >
                    <td className="py-2.5 pr-4 text-xs font-medium text-ic-text-primary">
                      {report.fund_name ?? '--'}
                    </td>
                    <td className="py-2.5 px-2 text-xs text-ic-text-secondary uppercase">
                      {report.regulation}
                    </td>
                    <td className="py-2.5 px-2 text-xs text-ic-text-secondary">
                      {formatFilingType(report.filing_type)}
                    </td>
                    <td className="py-2.5 px-2 font-mono text-xs text-ic-text-secondary">
                      {report.reporting_period}
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      <StatusBadge status={report.status} />
                    </td>
                    <td className="py-2.5 px-2 text-center font-mono text-xs text-ic-text-tertiary">
                      v{report.version}
                    </td>
                    <td className="py-2.5 pl-2 text-right text-xs text-ic-text-tertiary">
                      {report.created_at ? formatDate(report.created_at) : '--'}
                    </td>
                    <td className="py-2.5 pl-2 text-center">
                      <button
                        className="flex items-center gap-1 rounded px-2 py-1 text-[10px] font-medium text-ic-accent hover:bg-ic-accent/10 transition-colors mx-auto"
                        title="Download report data"
                      >
                        <Download className="h-3 w-3" />
                        JSON
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sub-Components ──────────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  loading?: boolean;
  highlight?: boolean;
}

function MetricCard({ label, value, icon, loading, highlight }: MetricCardProps) {
  if (loading) {
    return (
      <div className="rounded-lg border border-ic-border bg-ic-card p-4">
        <div className="flex items-center justify-between">
          <span className="text-ic-text-tertiary">{icon}</span>
        </div>
        <div className="mt-2 h-6 w-20 animate-pulse rounded bg-slate-700/50" />
        <div className="mt-1.5 h-3 w-16 animate-pulse rounded bg-slate-700/30" />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-ic-border bg-ic-card p-4">
      <div className="flex items-center justify-between">
        <span className="text-ic-text-tertiary">{icon}</span>
      </div>
      <p className={`mt-2 text-xl font-semibold tracking-tight ${
        highlight ? 'text-emerald-400' : 'text-ic-text-primary'
      }`}>
        {value}
      </p>
      <p className="mt-0.5 text-[10px] uppercase tracking-wider text-ic-text-tertiary">{label}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    active: 'bg-emerald-500/15 text-emerald-400',
    investing: 'bg-blue-500/15 text-blue-400',
    fundraising: 'bg-amber-500/15 text-amber-400',
    harvesting: 'bg-purple-500/15 text-purple-400',
    closed: 'bg-slate-500/15 text-slate-400',
    draft: 'bg-slate-500/15 text-slate-400',
    filed: 'bg-emerald-500/15 text-emerald-400',
    review: 'bg-amber-500/15 text-amber-400',
  };

  const classes = colorMap[status] ?? 'bg-slate-500/15 text-slate-400';

  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${classes}`}>
      {status}
    </span>
  );
}

function PercentBadge({ value }: { value: number }) {
  let color = 'text-slate-400';
  if (value >= 75) color = 'text-emerald-400';
  else if (value >= 40) color = 'text-amber-400';
  else if (value > 0) color = 'text-red-400';

  return (
    <span className={`font-mono text-xs font-semibold ${color}`}>
      {value > 0 ? `${value}%` : '--'}
    </span>
  );
}

function SkeletonBlock({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded bg-slate-700/50 ${className}`} />;
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex h-32 items-center justify-center">
      <p className="text-xs text-ic-text-tertiary">{message}</p>
    </div>
  );
}

// ─── Formatters ──────────────────────────────────────────────

function formatEur(amount: number): string {
  if (amount >= 1_000_000_000) return `\u20AC${(amount / 1_000_000_000).toFixed(1)}B`;
  if (amount >= 1_000_000) return `\u20AC${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `\u20AC${(amount / 1_000).toFixed(0)}K`;
  if (amount === 0) return '\u20AC0';
  return `\u20AC${amount.toLocaleString()}`;
}

function formatCompact(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(0)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return value.toString();
}

function formatFilingType(type: string): string {
  const map: Record<string, string> = {
    periodic: 'Quarterly',
    annual: 'Annual',
    pai_statement: 'PAI Statement',
    taxonomy: 'Taxonomy',
    entity_level: 'Entity Level',
    product_level: 'Product Level',
  };
  return map[type] ?? type;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    const day = d.getDate().toString().padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const mon = months[d.getMonth()];
    return `${day} ${mon ?? ''} ${d.getFullYear()}`;
  } catch {
    return iso;
  }
}
